import { useId } from 'react'

import { TAG_GROUPS, TAG_LABELS, type JournalTag } from '../../lib/selfReport'

interface TagChipsProps {
  value: readonly JournalTag[]
  onChange: (next: readonly JournalTag[]) => void
  /** Set when a stored tag is outside the eight -- see `hasUnknownTags`. */
  disabledReason?: string | null
}

/**
 * The eight, as a multi-select.
 *
 * ── REAL CHECKBOXES, CLIPPED ────────────────────────────────────────────────
 *
 * A native `<input type="checkbox">` inside the `<label>`, hidden with
 * `.visually-hidden` -- the technique `FileField` uses and for the same two
 * reasons. `display: none` and `visibility: hidden` remove the control from the
 * accessibility tree and break tapping it on iOS; `opacity: 0` leaves a hit
 * target where nothing is visible, which is what `audit:css` check C exists to
 * catch. Clipping keeps a real, focusable, space-toggleable checkbox with free
 * keyboard and screen-reader semantics, and the visible chip is styled from
 * `:checked` and `:focus-visible` siblings -- no `:has()`, no ARIA.
 *
 * A `<fieldset>`/`<legend>` rather than `role="group"`, because this genuinely is
 * a set of checkboxes and the native element is what assistive tech expects.
 */
export function TagChips({ value, onChange, disabledReason = null }: TagChipsProps) {
  const id = useId()
  const disabled = disabledReason !== null

  const toggle = (tag: JournalTag) => {
    const next = value.includes(tag) ? value.filter((t) => t !== tag) : [...value, tag]
    // Canonical order is applied on the way to the wire, not here: this keeps the
    // control's own state a plain reflection of what is ticked.
    onChange(next)
  }

  return (
    <fieldset className="chipset-field" disabled={disabled}>
      {/* `Tags`, not "Anything else about the day": the group this sits in is
          already titled `Anything else`, and a legend that restates its own
          container is read twice by a screen reader walking the form. R8
          permits the rename -- it names a control, not a measurement -- and no
          test or spec read the old string. */}
      <legend className="field__label">Tags</legend>
      {/* Two groups, one stored list (Sprint 1.9.11, concept §A). The nesting is
          `fieldset > fieldset`, which is how HTML says "a subset of these": each
          inner legend names its own time anchor, and a screen reader walking the
          form hears "Tags, Last night, Screens before bed". `TAG_GROUPS` is
          proved exhaustive at compile time in `lib/selfReport.ts`, so a tag can
          never fall out of the form by being left out of a group here. */}
      {TAG_GROUPS.map((group) => (
        <fieldset className="chipset-group" key={group.label}>
          <legend className="field__sublabel">{group.label}</legend>
          <div className="chipset">
            {group.tags.map((tag) => (
              <label className="chip chip--check" key={tag}>
                <input
                  type="checkbox"
                  className="visually-hidden chip__input"
                  checked={value.includes(tag)}
                  disabled={disabled}
                  onChange={() => toggle(tag)}
                />
                <span className="chip__face">{TAG_LABELS[tag]}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      {disabledReason && (
        <span className="field__hint" id={`${id}-why`}>
          {disabledReason}
        </span>
      )}
    </fieldset>
  )
}
